import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function QuoteForm() {
  const { t } = useTranslation('bardeau');
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.15);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gamme: '',
    message: '',
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    fetch(form.action, {
      method: form.method,
      body: data,
      headers: { Accept: 'application/json' },
    }).finally(() => {
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE('/merci');
      }
    });
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <section id="quote-form" className="bg-soft-gray py-16 md:py-20">
      <div
        ref={ref}
        className={`max-w-xl mx-auto px-4 md:px-6 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="bg-white rounded-xl p-6 md:p-10">
          <h3 className="font-manrope font-bold text-slate-primary text-xl md:text-2xl text-center mb-2">
            {t('form_h2')}
          </h3>
          <p className="text-gray-500 text-sm text-center mb-8">{t('form_subtitle')}</p>

          <form
            action="https://readdy.ai/api/form/d84bolf81o2e8gr2elrg"
            method="POST"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="ba-name" className="block text-sm font-medium text-slate-primary mb-1.5">
                  {t('form_name_label')}
                </label>
                <input
                  id="ba-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
                  placeholder={t('form_name_placeholder')}
                />
              </div>
              <div>
                <label htmlFor="ba-phone" className="block text-sm font-medium text-slate-primary mb-1.5">
                  {t('form_phone_label')}
                </label>
                <input
                  id="ba-phone"
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
                  placeholder={t('form_phone_placeholder')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="ba-email" className="block text-sm font-medium text-slate-primary mb-1.5">
                {t('form_email_label')}
              </label>
              <input
                id="ba-email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
                placeholder={t('form_email_placeholder')}
              />
            </div>

            <div>
              <label htmlFor="ba-gamme" className="block text-sm font-medium text-slate-primary mb-1.5">
                {t('form_gamme_label')}
              </label>
              <select
                id="ba-gamme"
                name="gamme"
                value={formData.gamme}
                onChange={(e) => handleChange('gamme', e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
              >
                <option value="">{t('form_gamme_select')}</option>
                <option value="standard">Standard (3 pattes)</option>
                <option value="architectural">Architectural</option>
                <option value="premium">Premium / Designer</option>
                <option value="non-sure">Je ne sais pas encore</option>
              </select>
            </div>

            <div>
              <label htmlFor="ba-message" className="block text-sm font-medium text-slate-primary mb-1.5">
                {t('form_message_label')}
              </label>
              <textarea
                id="ba-message"
                name="message"
                rows={4}
                maxLength={500}
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors resize-none"
                placeholder={t('form_message_placeholder')}
              />
              <p className="text-gray-400 text-xs mt-1 text-right">
                {formData.message.length}{t('form_char_count')}
              </p>
            </div>

            <input
              type="text"
              name="_gotcha"
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <button
              type="submit"
              className="w-full bg-copper hover:bg-copper-dark text-white font-bold py-4 rounded-md text-base transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {t('cta_button')}
              <i className="ri-arrow-right-line" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}